import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import { useCart } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import { Star, Heart, Minus, Plus, Truck, ShieldCheck, RotateCcw, Check, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "../components/store/ProductCard";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { add } = useCart();
  const { toggle, has } = useWishlist();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", body: "", author_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: d } = await apiClient.get(`/products/${slug}`);
      setData(d);
      setActiveImg(0);
      setQty(1);
      const r = await apiClient.get("/reviews", { params: { product_id: d.product.product_id } });
      setReviews(r.data);
      window.scrollTo({ top: 0 });
    })();
  }, [slug]);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 700);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-ink-muted">Loading…</div>;
  const { product, related } = data;
  const name = lang === "nl" ? product.name_nl : product.name_en;
  const desc = lang === "nl" ? product.description_nl : product.description_en;
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const lowStock = product.stock > 0 && product.stock < 5;

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/reviews", { ...reviewForm, product_id: product.product_id });
      const r = await apiClient.get("/reviews", { params: { product_id: product.product_id } });
      setReviews(r.data);
      setReviewForm({ rating: 5, title: "", body: "", author_name: "" });
      const { data: refreshed } = await apiClient.get(`/products/${slug}`);
      setData(refreshed);
      toast.success(lang === "nl" ? "Bedankt!" : "Thank you!");
    } catch {
      toast.error("Error");
    } finally {
      setSubmitting(false);
    }
  };

  const onAddToCart = () => {
    add(product, qty);
    toast.success(lang === "nl" ? "Toegevoegd aan winkelmand" : "Added to cart");
  };

  // delivery ETA
  const today = new Date();
  const eta = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
  const etaLabel = eta.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { weekday: "long", day: "numeric", month: "short" });

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10" data-testid="product-page">
      <div className="text-xs font-label text-ink-muted mb-4">
        <Link to="/" className="hover:text-ink">{t("home")}</Link> / <Link to="/shop" className="hover:text-ink">{t("nav_shop")}</Link> / <span className="text-ink">{name}</span>
      </div>
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="editorial-image bg-secondary-warm aspect-[4/5] overflow-hidden">
            <img src={product.images?.[activeImg]} alt={name} className="w-full h-full object-cover" />
          </div>
          {product.images?.length > 1 && (
            <div className="mt-3 flex gap-2">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 ${i === activeImg ? "border border-ink" : "border border-black/10"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-5">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-label text-ink-muted">{product.sku}</span>
            {onSale && <span className="bg-terracotta text-white font-label px-2 py-0.5">Sale</span>}
            {product.new_arrival && !onSale && <span className="bg-ink text-white font-label px-2 py-0.5">New</span>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light mt-2 leading-none">{name}</h1>

          {product.rating_count > 0 ? (
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-1 text-sm">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(product.rating_avg) ? "fill-terracotta text-terracotta" : "text-black/15"}`} />)}
                <span className="text-ink-muted ml-2">{product.rating_avg?.toFixed(1)} · {product.rating_count} reviews</span>
              </div>
            </div>
          ) : (
            <div className="mt-4 text-xs font-label text-ink-muted">{lang === "nl" ? "Wees de eerste die een review schrijft" : "Be the first to review"}</div>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-display text-4xl">€{product.price.toFixed(2)}</div>
            {onSale && <div className="text-ink-muted line-through">€{product.compare_at_price.toFixed(2)}</div>}
            {onSale && <span className="font-label text-terracotta">−{Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}%</span>}
          </div>
          <div className="text-xs text-ink-muted">{t("vat_incl")}</div>

          <p className="mt-6 leading-relaxed text-ink-muted">{desc}</p>

          {/* Urgency / scarcity */}
          {lowStock && (
            <div className="mt-5 flex items-center gap-2 text-sm bg-terracotta/8 text-terracotta px-3 py-2 border border-terracotta/20" data-testid="low-stock-warning">
              <Clock className="w-4 h-4" />
              {lang === "nl" ? `Nog maar ${product.stock} op voorraad — bestel snel` : `Only ${product.stock} left in stock — order soon`}
            </div>
          )}
          {!lowStock && product.stock > 0 && (
            <div className="mt-5 flex items-center gap-2 text-sm text-olive">
              <Check className="w-4 h-4" /> {lang === "nl" ? "Op voorraad" : "In stock"}
            </div>
          )}

          {/* Delivery ETA */}
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Truck className="w-4 h-4 text-ink" />
            <span>
              {lang === "nl" ? <>Voor 23:00 besteld, in huis <strong>{etaLabel}</strong></> : <>Order by 23:00 — arrives <strong>{etaLabel}</strong></>}
            </span>
          </div>

          {/* Social proof */}
          <div className="mt-3 flex items-center gap-2 text-xs text-ink-muted">
            <Users className="w-3.5 h-3.5" /> {lang === "nl" ? `${Math.floor(Math.random() * 30) + 22} mensen bekijken dit nu` : `${Math.floor(Math.random() * 30) + 22} people viewing this now`}
          </div>

          <div className="mt-7 flex items-center gap-3">
            <div className="flex items-center border border-ink">
              <button data-testid="qty-decrement" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 flex items-center justify-center hover:bg-secondary-warm"><Minus className="w-4 h-4" /></button>
              <div data-testid="qty-value" className="w-12 text-center font-display text-lg">{qty}</div>
              <button data-testid="qty-increment" onClick={() => setQty(qty + 1)} className="w-10 h-12 flex items-center justify-center hover:bg-secondary-warm"><Plus className="w-4 h-4" /></button>
            </div>
            <button
              data-testid="pdp-add-to-cart"
              disabled={product.stock === 0}
              onClick={onAddToCart}
              className="btn-primary text-sm flex-1"
            >
              {product.stock === 0 ? t("out_of_stock") : t("add_to_cart")}
            </button>
            <button data-testid="pdp-wishlist" onClick={() => toggle(product)} className="w-12 h-12 border border-ink flex items-center justify-center hover:bg-secondary-warm">
              <Heart className={`w-4 h-4 ${has(product.product_id) ? "fill-terracotta text-terracotta" : ""}`} />
            </button>
          </div>

          {/* Payment chips */}
          <div className="mt-6 pt-5 border-t border-black/10">
            <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Veilig betalen met" : "Pay securely with"}</div>
            <div className="flex flex-wrap gap-2">
              {["iDEAL", "Klarna", "Visa", "Mastercard", "PayPal", "Apple Pay", "Google Pay"].map((p) => (
                <span key={p} className="border border-black/15 px-2.5 py-1 text-[11px] font-label tracking-wider">{p}</span>
              ))}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="text-xs"><Truck className="w-4 h-4 text-terracotta mb-2" />{t("free_ship_nl")}</div>
            <div className="text-xs"><RotateCcw className="w-4 h-4 text-terracotta mb-2" />{t("returns")}</div>
            <div className="text-xs"><ShieldCheck className="w-4 h-4 text-terracotta mb-2" />{t("pay_secure")}</div>
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section className="mt-20 border-t border-black/10 pt-12 grid md:grid-cols-12 gap-10" data-testid="reviews-section">
        <div className="md:col-span-5">
          <h2 className="font-display text-3xl tracking-tighter font-light">{t("reviews")}</h2>
          <form onSubmit={submitReview} className="mt-6 space-y-3">
            <select value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })} data-testid="review-rating" className="border border-black/10 px-3 py-2 text-sm w-full">
              {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} ★</option>)}
            </select>
            <input required value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} data-testid="review-title" placeholder={lang === "nl" ? "Titel" : "Title"} className="border border-black/10 px-3 py-2 text-sm w-full" />
            <textarea required rows={3} value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })} data-testid="review-body" placeholder={lang === "nl" ? "Jouw mening" : "Your thoughts"} className="border border-black/10 px-3 py-2 text-sm w-full" />
            <input value={reviewForm.author_name} onChange={(e) => setReviewForm({ ...reviewForm, author_name: e.target.value })} data-testid="review-author" placeholder={t("yourname")} className="border border-black/10 px-3 py-2 text-sm w-full" />
            <button data-testid="review-submit" disabled={submitting} className="btn-primary text-sm w-full">{t("write_review")}</button>
          </form>
        </div>
        <div className="md:col-span-7 space-y-5">
          {reviews.length === 0 && <div className="text-ink-muted">{lang === "nl" ? "Nog geen reviews. Schrijf de eerste." : "No reviews yet. Be the first."}</div>}
          {reviews.map((r) => (
            <div key={r.review_id} className="border border-black/5 p-5 bg-white" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? "fill-terracotta text-terracotta" : "text-black/15"}`} />)}
                </div>
                <div className="text-xs text-ink-muted">{r.created_at?.slice(0, 10)}</div>
              </div>
              <div className="font-display text-lg mt-2">{r.title}</div>
              <p className="text-sm text-ink-muted mt-1.5 leading-relaxed">{r.body}</p>
              <div className="text-xs font-label mt-3">{r.author_name}</div>
            </div>
          ))}
        </div>
      </section>

      {related?.length > 0 && (
        <section className="mt-20 border-t border-black/10 pt-12">
          <h2 className="font-display text-3xl tracking-tighter font-light mb-8">{t("related")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </section>
      )}

      {/* Sticky add-to-cart bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-black/10 shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.1)] transition-transform duration-300 ${showSticky ? "translate-y-0" : "translate-y-full"}`}
        data-testid="sticky-pdp-bar"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 py-3 flex items-center gap-3 md:gap-6">
          <div className="w-12 h-14 md:w-14 md:h-16 bg-secondary-warm overflow-hidden shrink-0">
            <img src={product.images?.[0]} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm md:text-lg truncate">{name}</div>
            <div className="text-xs text-ink-muted">€{product.price.toFixed(2)}</div>
          </div>
          <div className="flex items-center border border-ink shrink-0">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-10 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
            <div className="w-8 text-center text-sm">{qty}</div>
            <button onClick={() => setQty(qty + 1)} className="w-8 h-10 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
          </div>
          <button data-testid="sticky-pdp-add" disabled={product.stock === 0} onClick={onAddToCart} className="btn-primary text-xs md:text-sm py-3 px-4 md:px-6">
            {product.stock === 0 ? t("out_of_stock") : t("add_to_cart")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
